import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiErrorDetail, tokenStorage } from "../lib/api";
import { clearAll as clearCache } from "../lib/dataCache";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = loading, false = guest, object = logged in
  const [user, setUser] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (alive) setUser(data);
      } catch {
        if (alive) {
          tokenStorage.clear();
          setUser(false);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Persist tokens for mobile (cookies may be blocked on iOS Safari ITP)
      if (data?.access_token) {
        tokenStorage.set(data.access_token, data.refresh_token);
      }
      // Clear any SPA cache belonging to a previous session (defense against
      // user-A → user-B swapping on the same device).
      clearCache();
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    tokenStorage.clear();
    clearCache();
    setUser(false);
  };

  const isAdmin = user && user !== false && user.role === "admin";
  const isClient = user && user !== false && user.role === "client";

  return (
    <AuthContext.Provider value={{ user, login, logout, isAdmin, isClient }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
